import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { AnalyticsContext, EVENTS, useIdentify, useTrack } from '@arrived/analytics';
import { useAuth } from '@arrived/auth';
import { stub } from '@arrived/common';
import { FEATURE_FLAGS, useFeature } from '@arrived/feature-flags';
import { trackMarketingConversionEvent } from '@arrived/marketing';
import { MARKETING_CONVERSION_EVENTS } from '@arrived/models';
import { useGetCustomerCountQuery } from '@arrived/queries';

import { SubscribeFooter, SubscribeFooterProps } from './SubscribeFooter';

type SubscribeFooterTextFeatureFlagValue = {
  id: number;
  message: string;
  cta: string;
};

export interface SubscribeFooterCustomProps
  extends Omit<SubscribeFooterProps, 'appBarProps' | 'cta' | 'in' | 'onSubscribe' | 'text'> {
  content?: {
    cta: SubscribeFooterProps['cta'];
    text: SubscribeFooterProps['text'];
  };
}

export interface SubscribeFooterContextType {
  /**
   * Indicates whether or not the SubscribeFooter is present on the page, this does not indicate if the footer is
   * showing as it is controlled by a number of other factors including the users lead/login status.
   */
  isOnPage: boolean;
  /**
   * Indicates whether or not the SubscribeFooter is showing. This takes into account isPresent and other attributes
   * which determine the visibility of the footer.
   */
  isShowing: boolean;
  /**
   * Can be used to override the default subscribe footer message & button text.
   */
  setCustomProps: Dispatch<SetStateAction<SubscribeFooterCustomProps | undefined>>;
  setIsOnPage: Dispatch<SetStateAction<boolean>>;
}

export const SubscribeFooterContext = createContext<SubscribeFooterContextType>({
  isOnPage: false,
  isShowing: false,
  setCustomProps: stub,
  setIsOnPage: stub,
});

export const SubscribeFooterWrapper = ({ children }: PropsWithChildren) => {
  const track = useTrack();
  const identify = useIdentify();
  const [isOnPage, setIsOnPage] = useState(false);
  const [customProps, setCustomProps] = useState<SubscribeFooterCustomProps>();
  const { isAuthenticated } = useAuth();
  const { hasAnonymousUserLoggedIn, hasAnonymousUserLeadCaptured } = useContext(AnalyticsContext);
  const customerCountState = useGetCustomerCountQuery();
  const intl = useIntl();
  const { isLoading } = useContext(AnalyticsContext);

  // TODO: These are all only needed for the A/B test of footer texts, once we decide on one we can remove these.
  const subscribeFooterText = useFeature<SubscribeFooterTextFeatureFlagValue>(FEATURE_FLAGS.SUBSCRIBE_FOOTER_TEXT);

  const isShowing = useMemo(
    () => isOnPage && !isAuthenticated && !hasAnonymousUserLoggedIn && !hasAnonymousUserLeadCaptured && !isLoading,
    [hasAnonymousUserLoggedIn, hasAnonymousUserLeadCaptured, isAuthenticated, isLoading, isOnPage]
  );

  useEffect(() => {
    if (subscribeFooterText.source !== 'experiment' || subscribeFooterText.value == null || !isShowing) {
      return;
    }

    track(EVENTS.EXPERIMENT_VIEWED, {
      experimentName: FEATURE_FLAGS.SUBSCRIBE_FOOTER_TEXT,
      experimentGroupNumber: subscribeFooterText.value.id,
    });
  }, [isShowing, subscribeFooterText, track]);

  const handleOnSubscribe = useCallback(
    (email: string) => {
      track(EVENTS.EMAIL_SUBSCRIPTION, { position: 'sticky-bar' });
      track(EVENTS.LEAD_FORM_COMPLETED, {
        email,
        lead_form_type: 'App - Home Page',
        referral_source: document.referrer,
        url: window.location.href,
      });
      identify({
        email,
        user_setup_stage: 'lead',
        lead_created_at: new Date().toISOString(),
      });
      trackMarketingConversionEvent({ email, type: MARKETING_CONVERSION_EVENTS.EMAIL_LEAD });
    },
    [identify, track]
  );

  const contextValue = useMemo(
    () => ({
      isOnPage,
      isShowing,
      setCustomProps,
      setIsOnPage,
    }),
    [isOnPage, isShowing]
  );

  const cta = useMemo(() => {
    if (customProps?.content) {
      return customProps.content.cta;
    }
    return isLoading ? undefined : subscribeFooterText.value?.cta;
  }, [customProps, isLoading, subscribeFooterText.value?.cta]);

  const text = useMemo(() => {
    if (customProps?.content) {
      return customProps.content.text;
    }
    if (isLoading || !customerCountState.isSuccess) {
      return;
    }
    return subscribeFooterText.value?.message.replaceAll(
      /\{count}/g,
      intl.formatNumber(customerCountState.data.count, { notation: 'compact' })
    );
  }, [customProps, intl, isLoading, subscribeFooterText.value?.message]);

  return (
    <SubscribeFooterContext.Provider value={contextValue}>
      {children}
      {cta && text && (
        <SubscribeFooter
          appBarProps={{ sx: { top: 'unset', bottom: 0 } }}
          cta={cta}
          in={isShowing}
          text={text}
          onSubscribe={handleOnSubscribe}
          {...customProps}
        />
      )}
    </SubscribeFooterContext.Provider>
  );
};
